import { useCallback, useEffect, useRef, useState } from "react"
import { Api } from "../../api/Assinafy"
import { Alert, Button, Icon, Input, Spinner } from "../../components"
import { Page, Section } from "../../layout"
import {
  ID_DOCUMENT_FACE_MATCH_ADVANCED,
  IDENTITY_STATUS,
  identityDeadEndMessage,
  isIdentityApproved,
  isIdentityDeadEnd,
} from "../../utils/identityVerification"
import "./identity-verification.scss"

const ASSINAFY_PRIVACY_URL = "https://www.assinafy.com.br/politica-de-privacidade"
// A LGPD exige identificar quem trata a imagem do documento e os dados biométricos.
// O parceiro é o operador; o aviso dele precisa estar ao alcance do signatário.
// TODO(produto/jurídico): confirmar a URL definitiva antes de ir para produção.
const PARTNER_PRIVACY_URL = "https://didit.me/privacy-policy"

const POLL_BACKOFF_MS = [2000, 3000, 5000, 8000, 13000, 21000, 30000]

/**
 * Verificação de identidade por documento de foto (métodos IdDocument*).
 *
 * Roda em /verify/{accessCode} — a mesma rota do link do e-mail e a rota para
 * onde o parceiro devolve o signatário. Nada na URL de retorno é confiável:
 * o estado é sempre relido da nossa API.
 */
const SignerIdentityVerification = ({ signerAccessCode, signer, onApproved }) => {
  const method = signer?.verification_method
  const isAdvanced = method === ID_DOCUMENT_FACE_MATCH_ADVANCED

  const [verification, setVerification] = useState(null)
  const [loading, setLoading] = useState(true)
  const [consentChecked, setConsentChecked] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState("")

  const continuedRef = useRef(false)

  const status = verification?.status || null
  const canStart = !!verification?.can_start
  const providerUrl = verification?.url || null
  const expiresAt = verification?.expires_at || null

  const expirationLabel = (() => {
    if (!expiresAt) return null
    const date = new Date(expiresAt)
    if (Number.isNaN(date.getTime())) return null
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(date)
  })()

  const fetchState = useCallback(
    ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true)
      }

      return Api("signer/identityVerification")(signerAccessCode)
        .then(({ data: { data } }) => {
          setVerification(data || {})
          setError("")
          return data
        })
        .catch(({ response }) => {
          setError(response?.data?.message || "Não foi possível consultar sua verificação de identidade.")
          return null
        })
        .finally(() => {
          if (!silent) {
            setLoading(false)
          }
        })
    },
    [signerAccessCode],
  )

  useEffect(() => {
    fetchState()
  }, [fetchState])

  // Pending ainda representa uma captura em processamento e continua sendo
  // consultado. InReview já libera o signatário para seguir com a assinatura.
  useEffect(() => {
    if (status !== IDENTITY_STATUS.PENDING) {
      return undefined
    }

    let canceled = false
    let timeoutId
    let attempt = 1

    const poll = async () => {
      await fetchState({ silent: true })
      if (!canceled) {
        const delay = POLL_BACKOFF_MS[Math.min(attempt, POLL_BACKOFF_MS.length - 1)]
        attempt += 1
        timeoutId = setTimeout(poll, delay)
      }
    }

    timeoutId = setTimeout(poll, POLL_BACKOFF_MS[0])
    return () => {
      canceled = true
      clearTimeout(timeoutId)
    }
  }, [status, fetchState])

  useEffect(() => {
    if (!isIdentityApproved(status) || continuedRef.current) {
      return undefined
    }

    continuedRef.current = true
    const timeoutId = window.setTimeout(() => onApproved?.(), 0)

    return () => window.clearTimeout(timeoutId)
  }, [status, onApproved])

  const handleContinueToSignature = () => {
    if (continuedRef.current) return

    continuedRef.current = true
    onApproved?.()
  }

  const handleStart = (restart = false) => {
    if ((!restart && !consentChecked) || starting) {
      return
    }

    setStarting(true)
    setError("")

    Api("signer/identityVerificationStart")(signerAccessCode)
      .then(({ data: { data } }) => {
        if (data?.url) {
          // Mesma aba: o parceiro devolve o signatário para /verify/{accessCode}.
          window.location.href = data.url
          return
        }
        setStarting(false)
        setError("Não foi possível abrir a verificação de identidade. Tente novamente em instantes.")
      })
      .catch(({ response }) => {
        setStarting(false)
        setError(response?.data?.message || "Não foi possível iniciar a verificação de identidade.")
        // 400 aqui costuma ser "já existe uma sessão": relê o estado para a tela
        // seguir para o andamento (ou para o beco sem saída) em vez de travar.
        fetchState({ silent: true })
      })
  }

  const renderShell = (children) => (
    <Page className="page-sign">
      <Section>
        <div className="identity-verification">
          <div className="identity-verification-content">{children}</div>
        </div>
      </Section>
    </Page>
  )

  if (loading && !verification) {
    return renderShell(
      <div className="identity-verification-loading">
        <Spinner />
        <p className="identity-verification-subtitle">Carregando sua verificação de identidade...</p>
      </div>,
    )
  }

  if (isIdentityApproved(status)) {
    return renderShell(
      <>
        <div className="identity-verification-illustration identity-verification-illustration-success">
          <Icon id="icon-success" size="40" />
        </div>
        <h1 className="identity-verification-title">Identidade confirmada</h1>
        <p className="identity-verification-subtitle">
          Estamos abrindo o seu documento.
        </p>
        <div className="identity-verification-loading">
          <Spinner />
        </div>
      </>,
    )
  }

  if (status === IDENTITY_STATUS.EXPIRED && canStart) {
    return renderShell(
      <>
        <div className="identity-verification-illustration identity-verification-illustration-denied">
          <Icon id="icon-warning" size="40" />
        </div>
        <h1 className="identity-verification-title">Link de verificação expirado</h1>
        <p className="identity-verification-subtitle">
          O prazo do link anterior terminou. Você pode iniciar uma nova captura sem nenhum custo adicional.
        </p>
        {error && <Alert variant="danger" className="identity-verification-alert">{error}</Alert>}
        <div className="identity-verification-actions">
          <Button variant="primary" size="lg" loading={starting} onClick={() => handleStart(true)}>
            INICIAR NOVAMENTE
          </Button>
        </div>
      </>,
    )
  }

  if (isIdentityDeadEnd(status)) {
    return renderShell(
      <>
        <div className="identity-verification-illustration identity-verification-illustration-denied">
          <Icon id="icon-denied" size="40" />
        </div>
        <h1 className="identity-verification-title">Verificação não concluída</h1>
        <p className="identity-verification-subtitle">{identityDeadEndMessage(status)}</p>
        {status !== IDENTITY_STATUS.DECLINED && (
          <Alert variant="warning" className="identity-verification-alert">
            Não é possível tentar de novo por aqui. Peça a quem enviou o documento
            {signer?.sender_name ? ` (${signer.sender_name})` : ""} uma nova solicitação de assinatura.
          </Alert>
        )}
      </>,
    )
  }

  if (status === IDENTITY_STATUS.IN_REVIEW) {
    return renderShell(
      <>
        <div className="identity-verification-illustration">
          <Icon id="icon-schedule" size="40" />
        </div>
        <h1 className="identity-verification-title">Verificação em análise</h1>
        <p className="identity-verification-subtitle">
          Sua identidade será analisada pelo remetente. Você já pode continuar e assinar o documento, mas ele será
          concluído e certificado apenas após a análise da sua identidade pelo remetente.
        </p>
        <div className="identity-verification-actions">
          <Button variant="primary" size="lg" onClick={handleContinueToSignature}>
            CONTINUAR PARA ASSINATURA
          </Button>
        </div>
      </>,
    )
  }

  if (status === IDENTITY_STATUS.PENDING) {
    return renderShell(
      <>
        <div className="identity-verification-illustration">
          <Icon id="icon-processing" size="40" />
        </div>
        <h1 className="identity-verification-title">Verificação em andamento</h1>
        <p className="identity-verification-subtitle">
          Assim que a análise terminar, o documento é liberado automaticamente nesta tela.
        </p>

        {error && <Alert variant="warning" className="identity-verification-alert">{error}</Alert>}

        <div className="identity-verification-actions">
          {providerUrl && (
            <Button variant="primary" size="lg" as="a" href={providerUrl}>
              CONTINUAR VERIFICAÇÃO
            </Button>
          )}
          <p className="identity-verification-polling">
            <Spinner /> Consultando o resultado...
          </p>
        </div>

        {expirationLabel && (
          <p className="identity-verification-note">Link de captura válido até {expirationLabel}.</p>
        )}

        <p className="identity-verification-note">
          Se você começou pelo computador e terminou pelo celular, pode deixar esta página aberta:
          ela é atualizada sozinha.
        </p>
      </>,
    )
  }

  if (!canStart) {
    // Sem sessão e sem poder criar: não há caminho pelo lado do signatário.
    return renderShell(
      <>
        <div className="identity-verification-illustration identity-verification-illustration-denied">
          <Icon id="icon-warning" size="40" />
        </div>
        <h1 className="identity-verification-title">Verificação indisponível</h1>
        <p className="identity-verification-subtitle">
          {error || "Não é possível iniciar a verificação de identidade para esta solicitação."}
        </p>
        <Alert variant="warning" className="identity-verification-alert">
          Peça a quem enviou o documento uma nova solicitação de assinatura.
        </Alert>
      </>,
    )
  }

  return renderShell(
    <>
      <div className="identity-verification-illustration">
        <Icon id="icon-verified_user" size="40" />
      </div>
      <h1 className="identity-verification-title">Confirme sua identidade</h1>
      <p className="identity-verification-subtitle">
        Para liberar este documento, a Assinafy precisa confirmar quem você é com uma foto do seu
        documento oficial
        e uma selfie.
      </p>

      <ol className="identity-verification-steps">
        <li>Tenha em mãos um documento oficial com foto (RG, CNH ou CIN).</li>
        <li>
          Você será levado para uma página segura de verificação, onde fotografa o documento
          e tira uma selfie.
        </li>
        <li>Ao voltar, o documento é liberado para assinatura.</li>
      </ol>

      <Alert variant="warning" className="identity-verification-alert">
        Se a análise for recusada ou abandonada, será preciso pedir uma nova solicitação de assinatura
        a quem enviou o documento. Links expirados podem ser iniciados novamente por esta tela.
      </Alert>

      {error && <Alert variant="danger" className="identity-verification-alert">{error}</Alert>}

      <label className="identity-verification-consent">
        <Input.Checkbox
          checked={consentChecked}
          onChange={(event) => setConsentChecked(event.target.checked)}
        />
        <span>
          Autorizo o tratamento, pela Assinafy{isAdvanced ? " e por seu parceiro de verificação de identidade" : ""}, da imagem do meu
          documento e dos meus dados biométricos com a finalidade de confirmar minha identidade nesta
          assinatura, conforme a{" "}
          <a href={ASSINAFY_PRIVACY_URL} target="_blank" rel="noreferrer">
            Política de Privacidade da Assinafy
          </a>{" "}
          {isAdvanced && (
            <>
              e o{" "}
              <a href={PARTNER_PRIVACY_URL} target="_blank" rel="noreferrer">
                aviso de privacidade do parceiro
              </a>
            </>
          )}
          .
        </span>
      </label>

      <div className="identity-verification-actions">
        <Button
          variant="primary"
          size="lg"
          disabled={!consentChecked}
          loading={starting}
          onClick={handleStart}
        >
          CONFIRMAR MINHA IDENTIDADE
        </Button>
      </div>
    </>,
  )
}

export default SignerIdentityVerification
